import { Buffer } from "buffer";
import { sha256 } from "@noble/hashes/sha256";
import bs58 from "bs58";
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID, createAssociatedTokenAccountIdempotentInstruction, createTransferCheckedInstruction, getAssociatedTokenAddressSync, } from "@solana/spl-token";
import { ComputeBudgetProgram, PublicKey, SystemProgram, Transaction, TransactionInstruction } from "@solana/web3.js";
import { ASSERTION_PACKET_RESERVE } from "./lighthouse.js";
import { assertWalletMessage } from "./walletMessage.js";
export const CREW_CONTRIBUTION_ESCROW_SEED = "crew_contribution_v1";
export const CREW_CONTRIBUTION_COMMITMENT_DOMAIN = "megacorp:crew-escrow:v1\0";
export const CREW_CONTRIBUTION_COMPUTE_UNITS = 100_000;
export const CREW_CONTRIBUTION_PRIORITY_MICROLAMPORTS = 100_000;
export const CREW_CONTRIBUTION_RESOURCE_DECIMALS = 6;
const memoProgram = new PublicKey("MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr");
const u64Max = 18446744073709551615n;
const i64Max = 9223372036854775807n;
const daySeconds = 86400n;
function invalid(field) { throw new Error(`crew_contribution_${field}_invalid`); }
function integer(value, field, maximum = u64Max) {
    if (typeof value !== "string" || !/^(0|[1-9][0-9]{0,19})$/.test(value))
        invalid(field);
    const n = BigInt(value);
    if (n > maximum)
        invalid(field);
    return n;
}
function key(value) {
    try {
        const result = new PublicKey(value);
        if (result.toBase58() !== value)
            invalid("address");
        return result;
    }
    catch {
        return invalid("address");
    }
}
function identifier(value) {
    if (typeof value !== "string" || !value.length || Buffer.byteLength(value, "utf8") > 128
        || value.trim() !== value || Buffer.from(value, "utf8").toString("utf8") !== value
        || /[\u0000-\u001f\u007f]/.test(value))
        invalid("identifier");
}
function hashId(value) { return Buffer.from(sha256(Buffer.from(value, "utf8"))); }
function u64(value) { const bytes = Buffer.alloc(8); bytes.writeBigUInt64LE(BigInt(value)); return bytes; }
export function validateCrewContribution(m) {
    if (m.version !== "SERVER_ESCROW_V1")
        invalid("version");
    for (const value of [m.receiptId, m.seasonId, m.roundId, m.crewId])
        identifier(value);
    if (!/^[a-z][a-z0-9_]{0,63}$/.test(m.resourceId) || !/^[a-f0-9]{64}$/.test(m.rosterHash)
        || /^0+$/.test(m.rosterHash))
        invalid("resource_or_roster");
    const addresses = [m.program, m.wallet, m.resourceMint, m.costWallet].map(key);
    const reserved = [SystemProgram.programId, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, memoProgram, ComputeBudgetProgram.programId];
    if (new Set(addresses.map(p => p.toBase58())).size !== addresses.length
        || addresses.some(p => reserved.some(r => r.equals(p)))
        || !PublicKey.isOnCurve(addresses[1].toBytes()) || !PublicKey.isOnCurve(addresses[3].toBytes()))
        invalid("parties");
    const amount = integer(m.resourceAmountBaseUnits, "amount"), revision = integer(m.revision, "revision");
    const budget = integer(m.budgetLamports, "budget", 100000000n);
    if (!amount || !revision || !budget || !Number.isSafeInteger(m.versusPointsRate)
        || m.versusPointsRate < 1 || m.versusPointsRate > 100 || amount * BigInt(m.versusPointsRate) > u64Max)
        invalid("amount_or_rate");
    const day = integer(m.dayStartUnix, "day", i64Max), week = integer(m.weekStartUnix, "week", i64Max);
    const expiry = integer(m.expiresAtUnix, "expiry", i64Max);
    const daysSinceMonday = ((day / daySeconds - 4n) % 7n + 7n) % 7n;
    if (day % daySeconds || week !== day - daysSinceMonday * daySeconds
        || expiry <= day || expiry > day + daySeconds)
        invalid("window");
}
/** Fixed binary layout for the program: domain, keys/hashes (32 bytes), integers LE, rate u8.
 * Identifier hashes are SHA-256 of their exact UTF-8 bytes, matching existing Crew receipts.
 * Nonnegative i64 timestamps have the same LE representation as u64 below.
 */
export function crewContributionCommitmentBytes(m) {
    validateCrewContribution(m);
    return Buffer.concat([
        Buffer.from(CREW_CONTRIBUTION_COMMITMENT_DOMAIN, "utf8"), key(m.program).toBuffer(), key(m.wallet).toBuffer(),
        hashId(m.receiptId), hashId(m.seasonId), hashId(m.roundId), hashId(m.crewId),
        u64(m.dayStartUnix), u64(m.weekStartUnix), Buffer.from(m.rosterHash, "hex"), u64(m.revision), u64(m.expiresAtUnix),
        hashId(m.resourceId), key(m.resourceMint).toBuffer(), u64(m.resourceAmountBaseUnits), Buffer.from([m.versusPointsRate]),
        key(m.costWallet).toBuffer(), u64(m.budgetLamports),
    ]);
}
export function crewContributionDigest(m) {
    return Buffer.from(sha256(crewContributionCommitmentBytes(m))).toString("hex");
}
export function crewContributionAddresses(m) {
    const digest = crewContributionDigest(m), program = key(m.program), owner = key(m.wallet), mint = key(m.resourceMint);
    const [escrowAuthority] = PublicKey.findProgramAddressSync([
        Buffer.from(CREW_CONTRIBUTION_ESCROW_SEED), owner.toBuffer(), hashId(m.receiptId), Buffer.from(digest, "hex"),
    ], program);
    return {
        escrowAuthority,
        ownerResourceAccount: getAssociatedTokenAddressSync(mint, owner),
        escrowResourceAccount: getAssociatedTokenAddressSync(mint, escrowAuthority, true),
    };
}
export function buildCrewContributionFunding(m, blockhash) {
    const digest = crewContributionDigest(m), owner = key(m.wallet), mint = key(m.resourceMint);
    const { escrowAuthority, ownerResourceAccount, escrowResourceAccount } = crewContributionAddresses(m);
    const transaction = new Transaction({ feePayer: owner, recentBlockhash: blockhash }).add(ComputeBudgetProgram.setComputeUnitLimit({ units: CREW_CONTRIBUTION_COMPUTE_UNITS }), ComputeBudgetProgram.setComputeUnitPrice({ microLamports: CREW_CONTRIBUTION_PRIORITY_MICROLAMPORTS }), createAssociatedTokenAccountIdempotentInstruction(owner, escrowResourceAccount, escrowAuthority, mint), createTransferCheckedInstruction(ownerResourceAccount, mint, escrowResourceAccount, owner, BigInt(m.resourceAmountBaseUnits), CREW_CONTRIBUTION_RESOURCE_DECIMALS), SystemProgram.transfer({ fromPubkey: owner, toPubkey: key(m.costWallet), lamports: BigInt(m.budgetLamports) }), new TransactionInstruction({ programId: memoProgram, keys: [], data: Buffer.from(`megacorp:crew:v1:${digest}`) }));
    const bytes = transaction.serialize({ requireAllSignatures: false, verifySignatures: false });
    if (bytes.length + ASSERTION_PACKET_RESERVE > 1232)
        invalid("packet_reserve");
    return transaction;
}
function decode(encoded) {
    if (typeof encoded !== "string" || encoded.length > 1644)
        invalid("wire");
    const bytes = Buffer.from(encoded, "base64");
    if (bytes.length > 1232 || bytes.toString("base64") !== encoded)
        invalid("wire");
    try {
        return Transaction.from(bytes);
    }
    catch {
        return invalid("wire");
    }
}
/** Validate the server's unsigned preparation before opening the wallet. */
export function validateCrewContributionPreparation(encoded, m) {
    const prepared = decode(encoded);
    const expected = buildCrewContributionFunding(m, prepared.recentBlockhash);
    if (!expected.serializeMessage().equals(prepared.serializeMessage())
        || prepared.signatures.some(s => s.signature !== null))
        invalid("preparation");
    return prepared;
}
/** Admit only the exact reviewed transfer plus supported wallet safety assertions. */
export function validateCrewContributionFunding(encoded, m, prepared) {
    const original = validateCrewContributionPreparation(prepared, m), approved = decode(encoded);
    assertWalletMessage(original.compileMessage(), approved.compileMessage());
    if (approved.signatures.length !== 1 || approved.signatures[0].publicKey.toBase58() !== m.wallet
        || !approved.verifySignatures())
        invalid("signature");
    return bs58.encode(approved.signature);
}
